import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { motion } from 'framer-motion';
import PageBanner from '../../components/PageBanner';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const OffRoadPage = () => {
    const { language } = useLanguage();
    const t = translations[language].offRoad;

    // Timeline data mapped to assets
    const timelineEvents = [
        { year: '1960-1963', image: '/assets/images/off-road/1960-1963.jpg', title: t.timeline.t1 },
        { year: '1966', image: '/assets/images/off-road/1966.jpg', title: t.timeline.t2 },
        { year: '1974', image: '/assets/images/off-road/1974.jpg', title: t.timeline.t3 },
        { year: '1999', image: '/assets/images/off-road/1999.jpg', title: t.timeline.t4 },
        { year: '2002', image: '/assets/images/off-road/2002-1.jpg', title: t.timeline.t5 },
        { year: '2008', image: '/assets/images/off-road/2008.jpg', title: t.timeline.t6 },
        { year: '2009', image: '/assets/images/off-road/2009.jpg', title: t.timeline.t7 },
        { year: '2013', image: '/assets/images/off-road/2013.jpg', title: t.timeline.t8 },
        { year: '2015', image: '/assets/images/off-road/2015-1.jpg', title: t.timeline.t9 },
        { year: 'Today', image: '/assets/images/off-road/today.png', title: t.timeline.t10 },
    ];

    const [activeYear, setActiveYear] = useState(timelineEvents[0].year);

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.8 }
    };

    return (
        <div className="bg-white min-h-screen pt-20 lg:pt-[120px] font-sans">
            <SEO
                title="Off-Road BAIC Vehicles Oman | Capability & Rugged Performance"
                description="Explore BAIC's off-road capability in the Oman, featuring advanced off-road SUVs by BAIC Oman"
                canonicalUrl="https://baicoman.com/history/off-road"
            />
            <h1 className="sr-only">Off-Road SUVs by BAIC Oman</h1>
            {/* Hero Section */}
            {/* Hero Section */}
            <PageBanner
                image="/assets/images/off-road/image-for-offroad.jpg"
                imageAlt="Off Road Hero"
            >
                <div className="w-full flex items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1 }}
                    >
                        <h4 className="text-sm font-bold text-gray-300 uppercase tracking-widest mb-3">{t.banner.subtitle}</h4>
                        <h1 className="text-2xl md:text-7xl font-bold text-white uppercase tracking-tight">{t.banner.title}</h1>
                    </motion.div>
                </div>
            </PageBanner>

            {/* Engineering Verification Ability */}
            <section className="py-24 max-w-7xl mx-auto px-4 overflow-hidden">
                <div className="flex flex-col md:flex-row items-center gap-16">
                    <motion.div
                        className="md:w-1/2"
                        {...fadeIn}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 uppercase mb-8 leading-tight">
                            {t.engineering.title}
                        </h2>
                        <p className="text-gray-600 leading-relaxed text-justify mb-6">
                            {t.engineering.p1}
                        </p>
                        <p className="text-gray-600 leading-relaxed text-justify">
                            {t.engineering.p2}
                        </p>
                    </motion.div>
                    <motion.div
                        className="md:w-1/2 relative"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        {/* RESTORED: User's updated image path */}
                        <div className="relative z-10">
                            <img src="/assets/images/off-road/375_1718954748verification-ability.png" alt="Verification Vehicle" className="w-full transform scale-x-[1]" />
                        </div>
                        {/* Ink splash effect simulation */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gray-900/10 rounded-full blur-3xl -z-10" />
                    </motion.div>
                </div>
            </section>

            {/* Vehicle Production Ability */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <div className="flex flex-col md:flex-row items-center gap-16">
                    <motion.div
                        className="md:w-1/2"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <img src="/assets/images/off-road/418_1718954749production-ability.png" alt="Production Ability" className="w-full" />
                    </motion.div>
                    <motion.div
                        className="md:w-1/2"
                        {...fadeIn}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 uppercase mb-8 leading-tight">
                            {t.production.title}
                        </h2>
                        <p className="text-gray-600 leading-relaxed text-justify">
                            {t.production.desc}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* High Designed Reliability - Dark Section */}
            <section className="bg-black text-white py-24 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <motion.div {...fadeIn} className="max-w-4xl mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold uppercase mb-8">{t.reliability.title}</h2>
                        <p className="text-gray-400 leading-loose text-justify text-lg">
                            {t.reliability.desc}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <img src="/assets/images/off-road/299_1718614797design-reliability.png" alt="High Reliability Engine" className="w-full rounded-lg shadow-2xl opacity-90" />
                    </motion.div>
                </div>
                {/* Background mesh/texture effect */}
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gray-900 to-transparent pointer-events-none" />
            </section>

            {/* History Timeline */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <div className="mb-16 text-center">
                    <img
                        src={timelineEvents.find(e => e.year === activeYear)?.image}
                        alt={`History ${activeYear}`}
                        className="w-full h-96 md:h-[500px] object-cover rounded-xl shadow-lg transition-all duration-500"
                    />
                    <h3 className="text-2xl font-bold mt-4 text-gray-800">{timelineEvents.find(e => e.year === activeYear)?.title}</h3>
                </div>

                {/* Timeline Slider */}
                <div className="relative flex items-center justify-between border-t border-gray-200 pt-8 mt-12 overflow-x-auto pb-4">
                    {timelineEvents.map((event, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveYear(event.year)}
                            className={`flex flex-col items-center gap-4 min-w-[100px] transition-all duration-300 group ${activeYear === event.year ? 'text-red-600 scale-110' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            <div className={`w-4 h-4 rounded-full border-2 transition-colors ${activeYear === event.year ? 'bg-red-600 border-red-600' : 'bg-white border-gray-300 group-hover:border-gray-500'}`} />
                            <span className="font-bold text-lg">{event.year}</span>
                        </button>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default OffRoadPage;
